import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip, Zoom } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SatelliteIcon from "@mui/icons-material/Satellite";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import moment from "moment";

import "./ReportDetails.scss";

import { Button, COLORS, Icon, Modal, Tab } from "abs-ui-components";

import ReportData from "./ReportData/ReportData";
import VesselEmissionOverview from "./VesselEmissionOverview/VesselEmissionOverview";
import Corrections from "./Corrections/Corrections";
import Verification from "./Verification/Verification";
import Documents from "./Documents/Documents";
import ReportVesselDetailsPage from "../../pages/MyFleet/ReportVesselDetails/ReportVesselDetailsPage";
import { reportsActions } from "../../store/reports/reports.slice";
import AbsModal from "../../common/absModal/AbsModal";
import GlobalReportExport from "./GlobalReportExport";
import ContactForm from "./ContactForm/ContactForm";
import axiosHttp from "../../services/interceptor";
import { accessAPIForResponse } from "../../services/api-services/api-implementations";
import { emitToasterMsg } from "../../services/toasterUtility";
import { debounce } from "lodash";
import { exportThetis, euMrvDebug, showThetisXmlWarning } from "../../constants/import_constants";
import { ClaimsContext } from "../../services/ClaimsService";
import { ClaimType } from "../../constants/claims";
import { FeatureType } from "../../constants/FeatureToggles";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { handleExportThetisXml } from "./ExportThetisXml";
const ReportDetails = (props) => {
	const { userHasClaim , userHasFeature } = useContext(ClaimsContext);
	const [selectedWaypointVersion, setSelectedWaypointVersion] = useState("Fuel2025");
	const currentYear = moment().year();
	const verificationStatuses = useSelector(
		(state) => state.reports.verificationStatus
	);
	const params = useParams();
	const reportMstId = params.id;
	const [showbreadCrumb, setShowBreadCrumb] = useState(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		return storedValue !== null ? JSON.parse(storedValue) : true;
	});

	const debouncedSetHeaderFooterEnabled = debounce((value) => {
		setShowBreadCrumb(value);
	}, 100);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [isExportReportModalOpen, setIsExportReportModalOpen] =
		useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [selectedTab, setActiveTab] = useState(0);
	const [reportDetails, setReportDetails] = useState(null);
	const [isContactModalOpen, setIsContactModalOpen] = useState(false);
	const [reportConfirmDelete, setReportConfirmDelete] = useState(false);
	const [reportFlags, setReportFlags] = useState(null);
	const [authorized, setAuthorized] = useState(true);
	const [confirmExport, setConfirmExport] = useState(false);
    const [exportAction, setExportAction] = useState(null);
	const isRefetchReportDetails = useSelector(
		(state) => state?.reports?.isRefetch
	);
	const IsDeleteEnabled = useSelector(
		(state) => state?.reports?.IsDeleteEnabled
	);
	const getReportingYear =
		Number(localStorage.getItem("selectedYear")) || currentYear;

	const user = useSelector((state) => state.userInfo.userData);
	dayjs.extend(relativeTime);
	// Use the debounced function
	useEffect(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		if (storedValue !== null) {
			debouncedSetHeaderFooterEnabled(JSON.parse(storedValue));
		}
	}, [debouncedSetHeaderFooterEnabled]);

	const handleOk = () => {
		navigate("/reports");
	};

	const subTabChangeHandler = (event) => {
		props?.handleSubTabChange(event);
	};
	const toggleExportReport = () => {
		setIsExportReportModalOpen(!isExportReportModalOpen);
	};

	const toggleContactForm = () => {
		setIsContactModalOpen(!isContactModalOpen);
	};

	const formatDate = (dateString) => {
		return moment(dateString).format("DD-MMM-YYYY");
	};

	const formattedDate = reportFlags?.VerificationCompleteDate
		? formatDate(reportFlags?.VerificationCompleteDate)
		: "";

	const icon = (() => {
		switch (reportFlags?.ReportStatus) {
			case "Verification Completed":
				return "check";
			case "No Data Submitted":
				return "info";
			case "Verification In Progress":
				return (
					<HourglassEmptyIcon
						sx={{ fontSize: 18, marginTop: "-6px" }}
					/>
				);
			case "Crosscheck In Progress":
				return (
					<HourglassEmptyIcon
						sx={{ fontSize: 18, marginTop: "-6px" }}
					/>
				);
			case "Supporting Documents Needed":
				return (
					<InsertDriveFileIcon
						sx={{ fontSize: 15, marginTop: "-6px" }}
					/>
				);
			default:
				return null;
		}
	})();

	const tooltipTitle =
		reportFlags?.ReportStatus === "Verification Completed" ? (
			<>
				Reviewer : <span className="font-style">{reportFlags?.Reviewer}</span>
				<br />
				Verifier : <span className="font-style">{reportFlags?.Verifier}</span>
				<br />
				Verification Complete Date : {formattedDate}
			</>
		) : reportFlags?.ReportStatus === "No Data Submitted" && reportFlags?.regeneratedDate ? (
			<>
				Regenerate completed <span className="font-style"> {dayjs(reportFlags?.regeneratedDate).fromNow()}</span>
			</>
		) : (
			""
		);

	const parentTabData = [
		{
			header: "Vessel Details",
			content: (
				<>
					{selectedTab === 0 && (
						<ReportVesselDetailsPage
							reportDetails={reportDetails}
						/>
					)}
				</>
			),
		},
		...(reportFlags?.ShowReportedData ?? true
			? [
				{
					header: "Reported Data",
					content: (
						<>
							{selectedTab === 1 && (
								<ReportData
									isParentReport={
										reportDetails?.IsParentReport
									}
									regulations={reportDetails?.Regulations}
									activeTab={selectedTab}
									handleSubTabChange={subTabChangeHandler}
									ParentReportMstId={
										reportDetails?.ParentReportMstId
									}
									showData={showbreadCrumb}
									isReportEnabled={reportFlags?.isReportEditable ? reportFlags?.isReportEditable : false}
								/>
							)}
						</>
					),
				},
			]
			: []),

		{
			header: "Documents",
			content: (
				<>
					{selectedTab === reportFlags?.ShowReportedData
						? 2
						: 1 && (
							<Documents
								reportDetails={reportDetails}
								showData={showbreadCrumb}
							/>
						)}
				</>
			),
		},

		...(showbreadCrumb && (reportFlags?.ShowReportedData ?? true)
			? [
				{
					header: "Vessel Emission Overview",
					content: (
						<div>
							<div>
								{selectedTab === 3 && (
									<VesselEmissionOverview
										activeTab={selectedTab}
										handleSubTabChange={
											subTabChangeHandler
										}
									/>
								)}
							</div>
						</div>
					),
				},
			]
			: []),

		{
			header: "Corrections",
			content: (
				<>
					{selectedTab === showbreadCrumb
						? 3
						: 4 && <Corrections reportDetails={reportDetails} />}
				</>
			),
		},
	];

	const childTabData = [
		{
			header: "Vessel Details",
			content: (
				<>
					{selectedTab === 0 && (
						<ReportVesselDetailsPage
							reportDetails={reportDetails}
							showData={showbreadCrumb}
						/>
					)}
				</>
			),
		},

		{
			header: "Reported Data",
			content: (
				<>
					{selectedTab === 1 && (
						<ReportData
							isParentReport={reportDetails?.IsParentReport}
							regulations={reportDetails?.Regulations}
							activeTab={selectedTab}
							handleSubTabChange={subTabChangeHandler}
							ParentReportMstId={reportDetails?.ParentReportMstId}
							showData={showbreadCrumb}
							isReportEnabled={reportFlags?.isReportEditable ? reportFlags?.isReportEditable : false}
						/>
					)}
				</>
			),
		},

		userHasClaim(ClaimType.VIEW_VERIFICATION_TAB)
			? {
				header: "Verification",
				content: (
					<>
						{selectedTab === 2 && (
							<Verification showData={showbreadCrumb} selectedWaypointVersion={selectedWaypointVersion} setSelectedWaypointVersion={setSelectedWaypointVersion} />
						)}
					</>
				),
			}
			: "",
	];

	let tabData = reportDetails?.IsParentReport ? parentTabData : childTabData;

	useEffect(() => {
		const refetchReportDetails = async () => {
			await accessAPIForResponse("GetReportDetailsByReportId", null, [
				reportMstId,
			]).then((response) => {
				if (response) {
					dispatch(
						reportsActions.setDeleteEnabled({
							IsDeleteEnabled: response.IsDeleteEnabled,
						})
					);
				}
			});
		};
		if (reportMstId) {
			refetchReportDetails();
		}
	}, [isRefetchReportDetails]);

	useEffect(() => {
		if (reportMstId) {
			getReportDetailsByReportId(reportMstId);
			GetReportFlagsByStatus(reportMstId);
		}
	}, []);

	const getReportDetailsByReportId = async (reportMasterId) => {
		await accessAPIForResponse("GetReportDetailsByReportId", null, [
			reportMasterId,
		]).then((response) => {
			if (response) {
				setReportDetails(response);
				dispatch(
					reportsActions.setSelectedReportDetails({
						selectedReportDetails: response,
					})
				);
			}
		});
	};

	const GetReportFlagsByStatus = async (reportMasterId) => {
		await accessAPIForResponse("GetReportFlags", null, [
			reportMasterId,
		]).then((response) => {
			setReportFlags(response);
			setAuthorized(response?.isAuthorized);
			dispatch(
				reportsActions.setReportFlagDetails({
					reportFlagDetails: response,
				})
			);
		});
	};

	if (verificationStatuses) {
		GetReportFlagsByStatus(reportMstId);
		dispatch(reportsActions.setVerificationStatus(false));
	}

	const tabChangeHandler = (event) => {
		setActiveTab(event);
		props?.onTabChange(tabData[event].header.toLowerCase());
	};

	const handleReturnToParentReport = () => {
		navigate(`/reports/${reportDetails?.ParentReportMstId}`);
		getReportDetailsByReportId(reportDetails?.ParentReportMstId);
		GetReportFlagsByStatus(reportDetails?.ParentReportMstId);
	};

	const callRegenerate = async () => {
		const parentMstId = reportDetails?.IsParentReport
			? reportMstId
			: reportDetails?.ReportMstId;
		axiosHttp({
			url: `api/Report/RegenerateReport?reportMstId=${parentMstId}`,
			method: "GET",
		})
			.then((res) => {
				emitToasterMsg(
					"success",
					"Parent Report",
					"Regeneration Queued"
				);
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const callMisstatement = async () => {
		const parentMstId = reportDetails?.IsParentReport
			? reportMstId
			: reportDetails?.ReportMstId;
		axiosHttp({
			url: `api/MisstatementValidation?reportMstId=${parentMstId}`,
			method: "POST",
		})
			.then((res) => {
				emitToasterMsg(
					"success",
					"Parent Report",
					"Regeneration Queued"
				);
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const handleMrvDebugData = async () => {
		try {
			const parentMstId = reportDetails?.IsParentReport
				? reportMstId
				: reportDetails?.ReportMstId;

			const exportUrl = `${euMrvDebug}${parentMstId}`;

			axiosHttp({
				url: exportUrl,
				method: "GET",
				responseType: "blob", // Ensure the response is treated as a binary file
			})
				.then((res) => {
					const blob = new Blob([res.data], {
						type: "application/zip",
					}); // Create a Blob from the response
					const url = window.URL.createObjectURL(blob); // Create a URL for the Blob

					const link = document.createElement("a");
					link.href = url;
					link.download = "exported_file.zip"; // Set the file name
					document.body.appendChild(link);
					link.click();

					// Clean up
					document.body.removeChild(link);
					window.URL.revokeObjectURL(url);

					emitToasterMsg(
						"success",
						"Export Successful",
						"The file has been exported successfully."
					);
				})
				.catch((error) => {
					emitToasterMsg(
						"error",
						"Export Failed",
						"Unable to export the file."
					);
				});
		} catch (error) {
			emitToasterMsg(
				"error",
				"Export Failed",
				"An unexpected error occurred during the export process."
			);
			console.error("Export Error:", error);
		}
	}

	const handleExportThetis = async () => {
		const parentMstId = reportDetails?.IsParentReport
			? reportMstId
			: reportDetails?.ReportMstId;

		await handleExportThetisXml({
			validationUrl: `${showThetisXmlWarning}${parentMstId}`,
			exportUrl: `${exportThetis}${parentMstId}`,
			axiosHttp,
			setShowConfirmation: setConfirmExport,
			setExportAction,
			emitToasterMsg,
		});
	};

	const handleDeletingParentReport = (reportMstId) => {
		axiosHttp({
			url: `api/Report/DeleteParentReport?reportMstId=${reportMstId}`,
			method: "GET",
		})
			.then((res) => {
				if (res?.data?.isSuccess) {
					emitToasterMsg(
						"success",
						"Parent Report",
						"Report Deleted Successfully"
					);
					navigate("/reports");
				} else {
					emitToasterMsg(
						"error",
						"Parent Report",
						res?.data?.errorCode
					);
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const handleExport = async () => {
		if (isExporting) {
			return;
		}

		setIsExporting(true);

		try {
			const reportId = reportDetails?.IsParentReport
				? reportMstId
				: reportDetails?.ReportMstId;

			const regulations = reportFlags?.Regulations || reportDetails?.Regulations;
			let exportUrl = "";
			const reportIdentifier =
				reportDetails?.ReportId ||
				`${regulations || "Report"}-${
					reportDetails?.VesselMst?.ImoNumber || "UNKNOWN"
				}-${getReportingYear}-${reportId}`;
			const sanitizedReportIdentifier = String(reportIdentifier)
				.trim()
				.replace(/[<>:"/\\|?*\x00-\x1F]/g, "-")
				.replace(/\s+/g, "-");
			let defaultFileName = `${sanitizedReportIdentifier}.xlsx`;

			if (regulations === "DCS") {
				exportUrl = `api/Report/ExportDcsDataByReportId?reportId=${reportId}&reportYear=${getReportingYear}`;
			} else if (regulations === "EUMRV") {
				exportUrl = `api/Report/ExportEuMrvDataByReportId?reportId=${reportId}&reportYear=${getReportingYear}`;
			}
			  else if (regulations === "FEU") {
				exportUrl = `api/FuelEu/FeuChildReportExport?reportMstId=${reportId}&reportYear=${getReportingYear}`;
			} 
			else if (regulations === "UKMRV") {
				exportUrl = `api/Report/ExportUkMrvDataByReportId?reportMstId=${reportId}`;
			}
			else if (reportDetails?.IsParentReport) {
				exportUrl = `api/Report/ExportReportDataByReportId?reportMstId=${reportId}`;
			}
			else if(regulations === "UKETS") {
				exportUrl = `api/Report/ExportUkEtsDataByReportId?reportMstId=${reportId}`;
			}
			else {
				emitToasterMsg(
					"error",
					"Export Failed",
					"Export is not supported for this report."
				);
				return;
			}

			const res = await axiosHttp({
				url: exportUrl,
				method: "GET",
				responseType: "blob",
			});

			const contentDisposition = res.headers["content-disposition"];
			let fileName = defaultFileName;
			if (contentDisposition) {
				const fileNameMatch = contentDisposition.match(
					/filename="?(.+)"?/i
				);
				if (fileNameMatch && fileNameMatch[1]) {
					fileName = fileNameMatch[1].replace(/"/g, "");
				}
			}

			const blob = new Blob([res.data], {
				type:
					res.headers["content-type"] ||
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = url;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();

			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			emitToasterMsg("success", "Export", "File downloaded successfully.");
		} catch (error) {
			emitToasterMsg(
				"error",
				"Export Failed",
				error?.response?.data?.message ||
					"An unexpected error occurred during the export process."
			);
			console.error("Export Error:", error);
		} finally {
			setIsExporting(false);
		}
	};

	const exportRegulations =
		reportFlags?.Regulations || reportDetails?.Regulations;
	const canExportCurrentReport = reportDetails?.IsParentReport
		? userHasFeature(FeatureType.ReportExport) &&
		  userHasClaim(ClaimType.VIEW_REPORT)
		: (exportRegulations === "DCS" &&
				userHasFeature(FeatureType.DcsExport) &&
				userHasClaim(ClaimType.EXPORT_DCS_DATA_REPORT)) ||
		  (exportRegulations === "EUMRV" &&
				userHasFeature(FeatureType.EuMrvExport) &&
				userHasClaim(ClaimType.EXPORT_EUMRV_DATA_REPORT)) ||
		  (exportRegulations === "FEU" &&
				userHasFeature(FeatureType.FeuExport)) ||
		  (exportRegulations === "UKMRV" &&
				userHasFeature(FeatureType.UkMrvExport) &&
				userHasClaim(ClaimType.VIEW_CHILD_REPORT))||
			 (exportRegulations === "UKETS" &&
				userHasFeature(FeatureType.UKETSExport) &&
				userHasClaim(ClaimType.VIEW_CHILD_REPORT));

	const handleDownloadforXMl = async () => {
		try {
			const exportUrl = `/api/ImoDcs/export-waypoint/${encodeURIComponent(
				reportDetails.ReportMstId
			)}?waypointVersion=${encodeURIComponent(selectedWaypointVersion)}`;

			const res = await axiosHttp({
				url: exportUrl,
				method: "GET",
				responseType: "blob",
			});

			const blob = new Blob([res.data], { type: "application/xml" });

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");

			link.href = url;
			link.download = "waypoint.xml";

			document.body.appendChild(link);
			link.click();

			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			emitToasterMsg("success", "The XML file has been downloaded successfully.");
		} catch (error) {
			console.error("Download Error:", error);

			emitToasterMsg("error", "Download Failed", "Unable to download the XML file.");
		}
	};
	return (
		<>
			{authorized ? (
				<div>
					<div className="mb-2 d-flex justify-content-between">
						<div className="report-info">
							<span className="header">
								{reportDetails?.ReportId}
							</span>
							{reportDetails?.VesselMst?.ImoNumber ? (
								<span className="text-gray ml-4 fw-bolder">
									{reportDetails?.VesselMst?.VesselName}, IMO#{" "}
									{reportDetails?.VesselMst?.ImoNumber}
								</span>
							) : (
								<span className="text-gray ml-4 fw-bolder">
									{reportDetails?.VesselMst?.VesselName}
								</span>
							)}
						</div>

						<div className="d-flex">
							{reportDetails?.ReportId &&
								!reportDetails?.IsParentReport ? (
								<>
								<Button
									variant="tertiary"
									startIcon={<Icon icon="arrow_back" />}
									className="mr-4"
									onClick={handleReturnToParentReport}
								>
									Return to Parent Report
								</Button>

										{userHasClaim(ClaimType.VIEW_VERIFICATION_TAB) &&
											reportDetails?.Regulations === "DCS" &&
											reportDetails?.VesselMst?.PortCodeMst?.CountryCode === "LR" && (
												<Button
													variant="tertiary"
													startIcon={<Icon icon="ios_share" />}
													className="mr-4"
													onClick={handleDownloadforXMl}
												>
													Download WayPoint XML
												</Button>
											)}

								{canExportCurrentReport && (
										<Button
											variant="tertiary"
											startIcon={
												isExporting ? (
													<HourglassEmptyIcon
														sx={{ fontSize: 18 }}        
													/>
												) : (
													<Icon icon="ios_share" />
												)
											}
											className="mr-4"
											onClick={handleExport}
											disabled={isExporting}
										>
											{isExporting
												? "Exporting..."
												: "Export Child Report"}
										</Button>
									)}							
								</>
							) : (
								<>
									<Button
										variant="tertiary"
										startIcon={<Icon icon="arrow_back" />}
										className="mr-4"
										onClick={() => {
											navigate("/reports");
										}}
									>
										Return to Report List
									</Button>

								 {canExportCurrentReport && ( 
										<Button
												variant="tertiary"
												startIcon={
													isExporting ? (
														<HourglassEmptyIcon
															sx={{ fontSize: 18 }}
														/>
													) : (
														<Icon icon="ios_share" />
													)
												}
												className="mr-4"
												onClick={handleExport}
												disabled={isExporting}
											>
												{isExporting
													? "Exporting..."
													: "Export Report"}
										</Button>
									 )}								 

									<Button
										variant="tertiary"
										startIcon={<SatelliteIcon />}
										className="mr-4"
										onClick={() => callRegenerate()}
									>
										Regenerate
									</Button>

									{reportDetails?.IsDeleteEnabled ? (
										<Button
											variant="tertiary"
											startIcon={<Icon icon="delete" />}
											className="mr-4"
											onClick={() =>
												setReportConfirmDelete(true)
											}
										>
											Delete Report
										</Button>
									) : (
										""
									)}
								</>
							)}
							{/* Future Scope */}
							{/* <Button
							variant="tertiary"
							startIcon={<Icon icon="contacts" />}
							className="mr-4"
							onClick={toggleContactForm}
						>
							Contact
						</Button>
						<Button
							variant="tertiary"
							startIcon={<Icon icon="ios_share" />}
							className="mr-4"
							onClick={toggleExportReport}
						>
							Export
						</Button> */}
							{(reportFlags?.Regulations === "EUMRV" || reportFlags?.Regulations === "FEU") &&
								getReportingYear >= 2024 && userHasFeature?.(FeatureType.ExportThetisXML) ? (
								<Button
									variant="tertiary"
									startIcon={<Icon icon="ios_share" />}
									className="mr-4"
									onClick={handleExportThetis}
								>
									Export Thetis XML
								</Button>
							) : (
								""
							)}
							{reportFlags?.Regulations === "EUMRV" &&
								getReportingYear >= 2024 && userHasClaim?.(ClaimType.VIEW_DEBUG_INFO) ? (
								<Button
									variant="tertiary"
									startIcon={<Icon icon="ios_share" />}
									className="mr-4"
									onClick={handleMrvDebugData}
								>
									Download Debug Data
								</Button>
							) : (
								""
							)}
							{reportFlags?.IsAisSuccess && (
								<Button
									variant="tertiary"
									startIcon={
										<SatelliteIcon sx={{ fontSize: 18 }} />
									}
									className="mr-4"
									disabled={true}
								>
									AIS Processed
								</Button>
							)}
							{reportFlags?.IsSampleVoyagesProcessed && (
								<Button
									variant="tertiary"
									startIcon={<Icon icon="check" />}
									className="mr-4"
									disabled={true}
								>
									Sample Voyages Processed
								</Button>
							)}

							{!reportDetails?.IsParentReport &&
								reportFlags?.ReportStatus && (
									<Tooltip
										title={tooltipTitle}
										disableHoverListener={!tooltipTitle}
										placement="top"
										TransitionComponent={Zoom}
										followCursor={true}
										arrow
										componentsProps={{
											tooltip: {
												sx: {
													fontSize: "13px",
													backgroundColor:
														"#012c51 !important",
													color: "white",
												},
											},
										}}
									>
										<span style={{display:'inline-flex', alignItems:'center'}}>
											<Button
												variant="tertiary"
												startIcon={
													icon && <Icon icon={icon} />
												}
												className="mr-4"
												disabled={true}
											>
												{reportFlags?.ReportStatus}
											</Button>
										</span>
									</Tooltip>
								)}
						</div>
					</div>

					<div className="report-details">
						{reportDetails && tabData && (
							<Tab
								tabData={tabData.map((tab) => ({
									...tab,
									header: (
										<span
											style={{
												textTransform: "capitalize",
											}}
										>
											{tab.header}
										</span>
									),
								}))}
								onTabChange={tabChangeHandler}
								activeTab={selectedTab}
							></Tab>
						)}
					</div>

					<div className="global-export p-4 m-4">
						<AbsModal
							className="p-4 m-4"
							isOpen={isExportReportModalOpen}
							title="Export File"
							onClose={setIsExportReportModalOpen}
							close={toggleExportReport}
						>
							{isExportReportModalOpen && (
								<GlobalReportExport
									onClose={setIsExportReportModalOpen}
								></GlobalReportExport>
							)}
						</AbsModal>
					</div>
					<div className="corrections-request-form">
						{isContactModalOpen && (
							<AbsModal
								isOpen={isContactModalOpen}
								title="Contact Information"
								onClose={setIsContactModalOpen}
								close={toggleContactForm}
							>
								<ContactForm />
							</AbsModal>
						)}
					</div>
					<div className="confirmation-modal-container">
						<Modal
							isOpen={reportConfirmDelete}
							modalHeader
							padding={"70px"}
							headerHeight={"50px"}
							showFooter
							onCloseClickListener={() => {
								setReportConfirmDelete(false);
							}}
							footer={
								<div className="d-flex justify-content-end fixed-action-btns p-4 footer">
									<Button
										className="delete-btn"
										onClick={() =>
											handleDeletingParentReport(
												reportDetails?.ReportMstId
											)
										}
										primaryColor={COLORS.RED.DARK}
										primaryBackgroundColor={
											COLORS.RED.LIGHT
										}
									>
										Delete
									</Button>
									<Button
										className="modal-cancel-btn"
										onClick={() => {
											setReportConfirmDelete(false);
										}}
									>
										Cancel
									</Button>
								</div>
							}
						>
							<div>
								Are you sure you want to delete the report?
							</div>
						</Modal>
					</div>
				</div>
			) : (
				<div>
					<Modal
						isOpen={!authorized}
						modalHeader
						padding={"70px"}
						headerHeight={"50px"}
						showFooter
						footer={
							<div className="d-flex justify-content-end fixed-action-btns p-4">
								<Button
									className="modal-delete-btn"
									onClick={handleOk}
									primaryColor={COLORS.RED.DARK}
									primaryBackgroundColor={COLORS.RED.LIGHT}
								>
									Ok
								</Button>
							</div>
						}
					>
						<div>You are not authorized to view this.</div>
					</Modal>
				</div>
			)}
			<Modal
				isOpen={confirmExport}
				modalHeader
				padding={"70px"}
				headerHeight={"50px"}
				showFooter
				onCloseClickListener={() => {
					setConfirmExport(false);
					setExportAction(null);
				}}
				footer={
					<div className="d-flex justify-content-end fixed-action-btns p-4">
						<Button
							className="modal-delete-btn"
							onClick={async () => {
								const action = exportAction;
								setConfirmExport(false);
								setExportAction(null);

								if (action) {
									await action();
								}
							}}
						>
							PROCEED
						</Button>

						<Button
							className="modal-cancel-btn ml-2"
							onClick={() => {
								setConfirmExport(false);
								setExportAction(null);
							}}
						>
							CANCEL
						</Button>
					</div>
				}
			>
				<div>
					The generated XML contains only{" "}
					{reportDetails?.Regulations === "FEU" ? "Fuel EU" : "EU MRV"} information.
					<br />
					<br />
					{reportDetails?.Regulations === "FEU" ? (
						<>
							An EU MRV report must also be created to generate combined
							EU MRV + FuelEU XML.
						</>
					) : (
						<>
							A FuelEU report must also be created to generate combined
							EU MRV + FuelEU XML.
						</>
					)}
					<br />
					<br />
					Do you want to proceed with{" "}
					{reportDetails?.Regulations === "FEU" ? "Fuel EU" : "EU MRV"} only?
				</div>
			</Modal>
		</>
	);
};

export default ReportDetails;
